import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/fr';

import auditService from '../../../services/audit.service';
import { superadminService } from '../../../services/superadmin.service';
import { theme } from '../../../theme';

moment.locale('fr');

const ProfessionalHistory = () => {
  const navigate = useNavigate();
  const { professionalId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);
  const [professional, setProfessional] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const load = async () => {
    try {
      setError('');
      setLoading(true);

      // Charger les infos du professionnel
      if (!professional) {
        const profRes = await superadminService.retrieveProfessional(professionalId);
        setProfessional(profRes?.data || null);
      }

      // Charger l'historique avec pagination
      const logsRes = await auditService.getUserLogs(professionalId, {
        page: page + 1,
        page_size: pageSize
      });
      const data = logsRes?.data;
      if (data?.results) {
        setLogs(data.results);
        setTotal(data.count || 0);
      } else {
        setLogs(data || []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [professionalId, page, pageSize]);

  const getActionColor = (action) => {
    if (action.includes('create')) return theme.colors.success;
    if (action.includes('update')) return theme.colors.info;
    if (action.includes('delete')) return theme.colors.error;
    if (action.includes('login')) return theme.colors.primary;
    if (action.includes('password')) return theme.colors.warning;
    return theme.colors.grey;
  };

  const getActionIcon = (action) => {
    if (action.includes('create')) return '+';
    if (action.includes('update')) return '✎';
    if (action.includes('delete')) return '×';
    if (action.includes('login')) return '→';
    if (action.includes('password')) return '🔑';
    return '•';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ color: theme.colors.error[600], fontWeight: 800 }}>{error}</Typography>;
  }

  return (
    <Box sx={{ pb: 12 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', color: theme.colors.text.primary }}>
            Historique - {professional?.first_name} {professional?.last_name}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
            {professional?.email}
          </Typography>
        </Box>
        <Tooltip title="Retour à la liste">
          <IconButton
            onClick={() => navigate('/superadmin/professionals')}
            sx={{
              backgroundColor: theme.colors.grey[100],
              color: theme.colors.text.secondary,
              '&:hover': { backgroundColor: theme.colors.grey[200] },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card sx={{ borderRadius: 3, border: `1px solid ${theme.colors.border.light}`, mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 2, color: theme.colors.text.primary }}>
            Statistiques
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                Total d'actions
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: theme.colors.text.primary }}>
                {total}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                Dernière activité
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: theme.colors.text.primary }}>
                {logs[0] ? moment(logs[0].timestamp).fromNow() : 'Aucune'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, border: `1px solid ${theme.colors.border.light}` }}>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 900 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography sx={{ py: 3, color: theme.colors.text.secondary }}>
                      Aucune action enregistrée
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const actionColor = getActionColor(log.action);
                  return (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {moment(log.timestamp).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>
                          {moment(log.timestamp).fromNow()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action_display}
                          size="small"
                          icon={<span>{getActionIcon(log.action)}</span>}
                          sx={{
                            backgroundColor: `${actionColor[500]}15`,
                            color: actionColor[700],
                            border: `1px solid ${actionColor[300]}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: theme.colors.text.primary }}>
                          {log.description}
                        </Typography>
                        {log.salon_id && (
                          <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>
                            Salon ID: {log.salon_id}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                          {log.ip_address || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: { xs: 0, md: '280px' },
          zIndex: 1000,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <Paper
            elevation={3}
            sx={{
              pointerEvents: 'auto',
              borderRadius: theme.borderRadius.xl,
              border: `1px solid ${theme.colors.border.light}`,
              overflow: 'hidden',
              width: 'fit-content'
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Lignes par page"
              sx={{ bgcolor: 'white' }}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ProfessionalHistory;
